/**
 * Packing (uc-core src/modules/pack/controllers/pack.controllers.ts):
 *   GET  /skus ?approvedAtLocationId&cropId&search                    skus:read
 *   GET  /label-versions ?skuId&status&premisesLocationId · /:id         label-versions:read
 *   POST /label-versions · /:id/approve · /:id/retire                   create / approve (quality release) / update
 *   GET  /packable-batches ?skuId&code → { data: [...] }                pack-runs:read
 *   POST /pack-runs { skuId, batchUnitId, plannedKg, locationId, equipmentId, operatorId, plannedDate }  pack-runs:create
 *   GET  /pack-runs ?status&locationId&batchUnitId · POST /pack-runs/:id/cancel { reason }
 *   GET  /pack-lots ?status&hold&skuId&search · /pack-lots/:idOrCode    pack-lots:read
 *   GET  /dispatches ?packLotId&search · /dispatches/:id · POST /dispatches/:id/corrections
 *   GET  /recall-export ?code&format=json|csv|pdf                       recalls:read
 * Kilos come as decimal strings and are kept as integer grams here.
 */
import { useQuery } from '@tanstack/react-query'
import { api, buildUrl, refreshTokens, tokenStore } from '@/api'
import { getList, getOne, getPage, useConsoleMutation, usePageQuery, type PageParams } from './core'
import { ck } from './keys'
import { arr, iso, kgToGrams, kgToGrams0, num, numOrNull, obj, str, strOrNull } from './mappers'
import { downloadBlob } from '../lib/download'
import type { LotBalance } from '../model/recall'

// ============================== products and labels ==============================

export interface Sku {
  id: string
  code: string
  name: string
  cropId: string
  cropName: string | null
  netG: number
  shelfLifeDays: number | null
  pouchesPerCarton: number | null
}
export const mapSku = (r: Record<string, unknown>): Sku => ({
  id: str(r.id),
  code: str(r.code),
  name: str(r.name),
  cropId: str(r.crop_id),
  cropName: strOrNull(r.crop_name),
  netG: num(r.net_g),
  shelfLifeDays: numOrNull(r.shelf_life_days),
  pouchesPerCarton: numOrNull(r.pouches_per_carton),
})
export function useSkus(f: { approvedAtLocationId?: string } = {}) {
  return useQuery({
    queryKey: [...ck.pack, 'skus', f],
    queryFn: () => getPage('/skus', { ...f, page: 1, limit: 100 }, mapSku).then((p) => p.rows),
    staleTime: 60_000,
  })
}

export interface LabelVersion {
  id: string
  skuId: string
  skuCode: string
  skuName: string
  code: string
  status: string
  premisesId: string
  premisesCode: string | null
  premisesName: string | null
  licenceNo: string | null
  licenceValidTo: string | null
  mrpInr: string | null
  allergen: string | null
  claimText: string | null
  claimPanel: string | null
  claimMinAnalytes: number | null
  claimMaxLoq: string | null
  artworkUrl: string | null
  approvedById: string | null
  approvedByName: string | null
  approvedAt: string | null
  createdAt: string | null
}
export const mapLabel = (r: Record<string, unknown>): LabelVersion => ({
  id: str(r.id),
  skuId: str(r.sku_id),
  skuCode: str(r.sku_code),
  skuName: str(r.sku_name),
  code: str(r.code),
  status: str(r.status, 'DRAFT'),
  premisesId: str(r.premises_location_id),
  premisesCode: strOrNull(r.premises_code),
  premisesName: strOrNull(r.premises_name),
  licenceNo: strOrNull(r.fssai_licence_no),
  licenceValidTo: strOrNull(r.fssai_licence_valid_to),
  mrpInr: strOrNull(r.mrp_inr),
  allergen: strOrNull(r.allergen_statement),
  claimText: strOrNull(r.claim_text),
  claimPanel: strOrNull(r.claim_panel),
  claimMinAnalytes: numOrNull(r.claim_min_analytes),
  claimMaxLoq: strOrNull(r.claim_max_loq),
  artworkUrl: strOrNull(r.artwork_url),
  approvedById: strOrNull(r.approved_by_id),
  approvedByName: strOrNull(r.approved_by_name),
  approvedAt: iso(r.approved_at),
  createdAt: iso(r.created_at),
})
export function useLabelVersions(
  f: PageParams & { skuId?: string; status?: string; premisesLocationId?: string } = {},
) {
  return usePageQuery([...ck.pack, 'labels', f], () =>
    getPage('/label-versions', { page: 1, limit: 100, ...f }, mapLabel),
  )
}

const LABELS_TOUCHED = [
  [...ck.pack, 'labels'],
  [...ck.pack, 'skus'],
  [...ck.pack, 'packable'],
]

export function useApproveLabel() {
  return useConsoleMutation<string, LabelVersion>({
    mutationFn: (id) =>
      api
        .post<Record<string, unknown>>(`/label-versions/${id}/approve`)
        .then((r) => mapLabel(obj(r))),
    invalidate: LABELS_TOUCHED,
    success: (l) => `${l.code} approved`,
    errorTitle: 'Not approved',
  })
}
export function useRetireLabel() {
  return useConsoleMutation<string>({
    mutationFn: (id) => api.post(`/label-versions/${id}/retire`),
    invalidate: LABELS_TOUCHED,
    success: 'Draft withdrawn',
    errorTitle: 'Not withdrawn',
  })
}
export interface NewLabelBody {
  skuId: string
  code: string
  premisesLocationId: string
  mrpInr: string
  allergenStatement?: string
  claimText?: string
  claimPanel?: string
  claimMinAnalytes?: number
  claimMaxLoq?: string
  artworkUrl?: string
}
export function useCreateLabel() {
  return useConsoleMutation<NewLabelBody>({
    mutationFn: (body) => api.post('/label-versions', body),
    invalidate: LABELS_TOUCHED,
    success: (_d, v) => `${v.code} uploaded: waiting for approval`,
    errorTitle: 'Label not saved',
  })
}

// ============================== allocate (F7.1) ==============================

export interface Packable {
  unitId: string
  code: string
  eligible: boolean
  reasons: string[]
  cropId: string | null
  farmerId: string | null
  availableG: number
  sacks: number
  places: string[]
  labStatus: string | null
  labUnitId: string | null
  leftoverOf: string | null
  parentLotCodes: string[]
}
export const mapPackable = (r: Record<string, unknown>): Packable => ({
  unitId: str(r.unit_id),
  code: str(r.code),
  eligible: r.eligible === true,
  reasons: arr<string>(r.reasons).map(String),
  cropId: strOrNull(r.crop_id),
  farmerId: strOrNull(r.farmer_id),
  availableG: kgToGrams0(r.available_kg as string),
  sacks: num(r.sacks),
  places: arr<string>(r.sacks_location_codes).map(String),
  labStatus: strOrNull(r.lab_status),
  labUnitId: strOrNull(r.lab_unit_id),
  leftoverOf: strOrNull(r.leftover_of_batch_code),
  parentLotCodes: arr<string>(r.parent_lot_codes).map(String),
})
export function usePackable(skuId: string | null) {
  return useQuery({
    queryKey: [...ck.pack, 'packable', skuId],
    enabled: Boolean(skuId),
    queryFn: () => getList('/packable-batches', { skuId }, mapPackable),
  })
}
/** One batch by code, with the reasons it cannot be packed ("Not in the list? Search a batch code"). */
export function lookupPackable(code: string): Promise<Packable | null> {
  return getList('/packable-batches', { code: code.trim() }, mapPackable).then((l) => l[0] ?? null)
}

export interface PackRun {
  id: string
  code: string
  status: string
  locationCode: string | null
  equipmentCode: string | null
  operatorId: string | null
  operatorName: string | null
  batchUnitId: string | null
  batchCode: string | null
  skuId: string | null
  plannedG: number | null
  plannedPouches: number | null
  plannedDate: string | null
  startedAt: string | null
  endedAt: string | null
  inputG: number | null
  outputG: number | null
  packLotId: string | null
  packLotCode: string | null
  createdAt: string | null
}
export const mapPackRun = (r: Record<string, unknown>): PackRun => {
  const p = obj(r.params)
  return {
    id: str(r.id),
    code: str(r.code),
    status: str(r.status),
    locationCode: strOrNull(r.location_code),
    equipmentCode: strOrNull(r.equipment_code),
    operatorId: strOrNull(r.operator_id),
    operatorName: strOrNull(r.operator_name),
    batchUnitId: strOrNull(r.origin_unit_id),
    batchCode: strOrNull(r.batch_code),
    skuId: strOrNull(p.sku_id),
    plannedG: kgToGrams(p.planned_kg as string),
    plannedPouches: numOrNull(p.planned_pouches),
    plannedDate: strOrNull(p.planned_date),
    startedAt: iso(r.started_at),
    endedAt: iso(r.ended_at),
    inputG: kgToGrams(r.input_kg as string),
    outputG: kgToGrams(r.output_kg as string),
    packLotId: strOrNull(r.pack_lot_id),
    packLotCode: strOrNull(r.pack_lot_code),
    createdAt: iso(r.created_at),
  }
}
export function usePackRuns(f: PageParams & { status?: string; batchUnitId?: string } = {}) {
  return usePageQuery([...ck.pack, 'runs', f], () =>
    getPage('/pack-runs', { page: 1, limit: 50, ...f }, mapPackRun),
  )
}

export function usePackRun(id: string | null | undefined) {
  return useQuery({
    queryKey: [...ck.pack, 'runs', 'detail', id],
    enabled: Boolean(id),
    queryFn: () =>
      getOne(`/pack-runs/${id}`, (r) => ({
        ...mapPackRun(r),
        cleanoutById: strOrNull(r.cleanout_by_id),
        balance: mapRunBalance(r.balance),
      })),
  })
}

export interface CreatePackRunBody {
  skuId: string
  batchUnitId: string
  plannedKg: string
  locationId: string
  equipmentId: string
  operatorId: string
  plannedDate: string
}
const RUNS_TOUCHED = [
  [...ck.pack, 'runs'],
  [...ck.pack, 'packable'],
  [...ck.pack, 'lots'],
]
export function useCreatePackRun() {
  return useConsoleMutation<CreatePackRunBody, { code: string; plannedPouches: number }>({
    mutationFn: (body) =>
      api
        .post<Record<string, unknown>>('/pack-runs', body)
        .then((r) => ({ code: str(obj(r).code), plannedPouches: num(obj(r).planned_pouches) })),
    invalidate: RUNS_TOUCHED,
    success: (r) => `Pack run ${r.code} created · ${r.plannedPouches} pouches planned`,
    errorTitle: 'Pack run not created',
  })
}
export function useCancelPackRun() {
  return useConsoleMutation<{ id: string; reason: string }>({
    mutationFn: ({ id, reason }) => api.post(`/pack-runs/${id}/cancel`, { reason }),
    invalidate: RUNS_TOUCHED,
    success: 'Pack run cancelled',
    errorTitle: 'Not cancelled',
  })
}

/** The packing hall's lines (equipment at the hall whose steps include PACK). */
export interface PackLine {
  id: string
  code: string
  name: string
  steps: string[]
}
export function usePackLines(locationId: string | null) {
  return useQuery({
    queryKey: [...ck.pack, 'lines', locationId],
    enabled: Boolean(locationId),
    queryFn: () =>
      getPage('/equipment', { locationId, page: 1, limit: 100 }, (r): PackLine => ({
        id: str(r.id),
        code: str(r.code),
        name: str(r.name),
        steps: arr<string>(obj(r.specifications).step_codes).map(String),
      })).then((p) => p.rows),
    staleTime: 5 * 60_000,
  })
}
/** A pack line: the hall's equipment that lists PACK among its steps (else the only one there). */
export function pickPackLine(lines: ReadonlyArray<PackLine>): PackLine | null {
  return (
    lines.find((l) => l.steps.includes('PACK')) ?? (lines.length === 1 ? (lines[0] ?? null) : null)
  )
}

// ============================== pack lots ==============================

export interface RunBalance {
  inputs: number | null
  inputG: number | null
  outputG: number | null
  productG: number | null
  residualG: number | null
  residualPct: number | null
  lossLimitPct: number | null
  status: string | null
}
export const mapRunBalance = (r: unknown): RunBalance | null => {
  if (!r || typeof r !== 'object') return null
  const b = obj(r)
  return {
    inputs: numOrNull(b.inputs),
    inputG: kgToGrams(b.input_kg as string),
    outputG: kgToGrams(b.output_kg as string),
    productG: kgToGrams(b.product_kg as string),
    residualG: kgToGrams(b.residual_kg as string),
    residualPct: numOrNull(b.residual_pct),
    lossLimitPct: numOrNull(b.loss_limit_pct),
    status: strOrNull(b.status),
  }
}

export interface PackLotRow {
  id: string
  code: string
  status: string
  hold: string
  qrUrl: string | null
  packDate: string | null
  bestBefore: string | null
  pouchCount: number | null
  labelsPrinted: number | null
  unitId: string
  runId: string
  runCode: string | null
  runStatus: string | null
  operatorId: string | null
  operatorName: string | null
  cleanoutById: string | null
  cleanoutByName: string | null
  skuId: string
  skuCode: string
  skuName: string
  netG: number
  pouchesPerCarton: number | null
  labelVersionId: string
  labelCode: string
  batchUnitId: string | null
  batchCode: string | null
  packedG: number | null
  pouchesDispatched: number
}
export const mapPackLotRow = (r: Record<string, unknown>): PackLotRow => ({
  id: str(r.id),
  code: str(r.code),
  status: str(r.status),
  hold: str(r.hold, 'NONE'),
  qrUrl: strOrNull(r.qr_url),
  packDate: strOrNull(r.pack_date),
  bestBefore: strOrNull(r.best_before),
  pouchCount: numOrNull(r.pouch_count),
  labelsPrinted: numOrNull(r.labels_printed),
  unitId: str(r.unit_id),
  runId: str(r.run_id),
  runCode: strOrNull(r.run_code),
  runStatus: strOrNull(r.run_status),
  operatorId: strOrNull(r.operator_id),
  operatorName: strOrNull(r.operator_name),
  cleanoutById: strOrNull(r.cleanout_by_id),
  cleanoutByName: strOrNull(r.cleanout_by_name),
  skuId: str(r.sku_id),
  skuCode: str(r.sku_code),
  skuName: str(r.sku_name),
  netG: num(r.net_g),
  pouchesPerCarton: numOrNull(r.pouches_per_carton),
  labelVersionId: str(r.label_version_id),
  labelCode: str(r.label_code),
  batchUnitId: strOrNull(r.batch_unit_id),
  batchCode: strOrNull(r.batch_code),
  packedG: kgToGrams(r.packed_kg as string),
  pouchesDispatched: num(r.pouches_dispatched),
})

export interface PackLotDetail extends PackLotRow {
  pouchesLeft: number
  sellable: boolean
  labOverall: string | null
  labReportIds: string[]
  balance: RunBalance | null
  checks: Array<{ bizStep: string; occurredAt: string | null; payload: Record<string, unknown> }>
  exceptions: Array<{
    id: string
    type: string
    severity: string
    status: string
    detail: Record<string, unknown>
    raisedAt: string | null
  }>
  decisions: Array<{
    id: string
    decision: string
    auto: boolean
    reason: string | null
    decidedById: string | null
    decidedAt: string | null
  }>
  dispatches: Array<{
    shipmentId: string
    shipmentCode: string
    toOrg: string | null
    challanNo: string | null
    pouches: number
    dispatchedAt: string | null
  }>
}
export const mapPackLotDetail = (r: Record<string, unknown>): PackLotDetail => {
  const lab = obj(r.lab_status)
  return {
    ...mapPackLotRow(r),
    pouchesLeft: num(r.pouches_left),
    sellable: r.sellable === true,
    labOverall: strOrNull(lab.overall),
    labReportIds: arr<string>(lab.report_ids).map(String),
    balance: mapRunBalance(r.balance),
    checks: arr(r.checks).map((c) => ({
      bizStep: str(c.biz_step),
      occurredAt: iso(c.occurred_at),
      payload: obj(c.payload),
    })),
    exceptions: arr(r.exceptions).map((e) => ({
      id: str(e.id),
      type: str(e.type),
      severity: str(e.severity),
      status: str(e.status),
      detail: obj(e.detail),
      raisedAt: iso(e.raised_at),
    })),
    decisions: arr(r.decisions).map((d) => ({
      id: str(d.id),
      decision: str(d.decision),
      auto: d.auto === true,
      reason: strOrNull(d.reason),
      decidedById: strOrNull(d.decided_by_id),
      decidedAt: iso(d.decided_at),
    })),
    dispatches: arr(r.dispatches).map((d) => ({
      shipmentId: str(d.shipment_id),
      shipmentCode: str(d.shipment_code),
      toOrg: strOrNull(d.to_org),
      challanNo: strOrNull(d.challan_no),
      pouches: num(d.pouches),
      dispatchedAt: iso(d.dispatched_at),
    })),
  }
}
export function usePackLots(
  f: PageParams & { status?: string; hold?: string; search?: string } = {},
) {
  return usePageQuery([...ck.pack, 'lots', f], () =>
    getPage('/pack-lots', { page: 1, limit: 50, ...f }, mapPackLotRow),
  )
}
export function usePackLot(idOrCode: string | null | undefined) {
  return useQuery({
    queryKey: [...ck.pack, 'lots', 'detail', idOrCode],
    enabled: Boolean(idOrCode),
    queryFn: () => getOne(`/pack-lots/${encodeURIComponent(idOrCode!)}`, mapPackLotDetail),
  })
}

// ============================== dispatches (F7.8) ==============================

export interface DispatchItem {
  itemId: string
  packLotId: string
  code: string
  skuName: string | null
  netG: number
  pouches: number
  loadedAt: string | null
}
export interface Dispatch {
  id: string
  code: string
  status: string
  toOrgId: string | null
  toOrg: string | null
  challanNo: string | null
  vehicleNo: string | null
  dispatchedAt: string | null
  fromLocation: string | null
  items: DispatchItem[]
}
export const mapDispatch = (r: Record<string, unknown>): Dispatch => ({
  id: str(r.id),
  code: str(r.code),
  status: str(r.status),
  toOrgId: strOrNull(r.to_org_id),
  toOrg: strOrNull(r.to_org),
  challanNo: strOrNull(r.challan_no),
  vehicleNo: strOrNull(r.vehicle_no),
  dispatchedAt: iso(r.dispatched_at),
  fromLocation: strOrNull(r.from_location),
  items: arr(r.items).map((i) => ({
    itemId: str(i.item_id),
    packLotId: str(i.pack_lot_id),
    code: str(i.code),
    skuName: strOrNull(i.sku_name),
    netG: num(i.net_g),
    pouches: num(i.pouches),
    loadedAt: iso(i.loaded_at),
  })),
})
export function useDispatches(f: PageParams & { search?: string; packLotId?: string } = {}) {
  return usePageQuery([...ck.pack, 'dispatches', f], () =>
    getPage('/dispatches', { page: 1, limit: 50, ...f }, mapDispatch),
  )
}
export function useDispatch(id: string | undefined) {
  return useQuery({
    queryKey: [...ck.pack, 'dispatches', 'detail', id],
    enabled: Boolean(id),
    queryFn: () => getOne(`/dispatches/${id}`, mapDispatch),
  })
}
export function useDispatchCorrection() {
  return useConsoleMutation<{ id: string; packLot: string; pouches: number; reason: string }>({
    mutationFn: ({ id, ...body }) => api.post(`/dispatches/${id}/corrections`, body),
    invalidate: [
      [...ck.pack, 'dispatches'],
      [...ck.pack, 'lots'],
    ],
    success: 'Correction added to the trip',
    errorTitle: 'Correction not saved',
  })
}

// ============================== mock recall (F7.9) ==============================

export interface RecallExport {
  code: string
  farmers: Array<{ farmerId: string; name: string | null; farmerCode: string | null }>
  lots: Array<{
    unitId: string
    code: string
    farmerId: string | null
    seasonCode: string | null
    netG: number | null
    hold: string
    status: string
  }>
  runs: Array<{
    id: string
    code: string
    stepCode: string
    status: string
    startedAt: string | null
    endedAt: string | null
    notClosed: boolean
  }>
  batches: Array<{
    unitId: string
    code: string
    stream: string
    status: string
    hold: string
    netG: number | null
  }>
  otherOutputs: Array<{
    unitId: string
    code: string
    unitType: string
    stream: string
    netG: number | null
  }>
  packLots: Array<{
    id: string
    code: string
    pouches: number | null
    status: string
    hold: string
    pouchesDispatched: number
  }>
  customers: Array<{
    orgId: string
    org: string
    packLotCode: string
    challanNo: string
    pouches: number
    dispatchedAt: string | null
    shipmentCode: string
  }>
  balance: LotBalance[]
  elapsedMs: number | null
  generatedAt: string | null
  generatedBy: { id: string; name: string; username: string } | null
  start: { code: string; unitType: string | null; packLotId: string | null }
}
const g = (v: unknown) => kgToGrams(v as string)
export const mapLotBalance = (b: Record<string, unknown>): LotBalance => ({
  lotId: str(b.lot_id),
  lotCode: str(b.lot_code),
  gateG: kgToGrams0(b.gate_net_kg as string),
  inLotBagsG: kgToGrams0(b.in_lot_bags_kg as string),
  brandBatchG: kgToGrams0(b.brand_batch_kg as string),
  convBatchG: kgToGrams0(b.conv_batch_kg as string),
  byproductG: kgToGrams0(b.byproduct_kg as string),
  wasteG: kgToGrams0(b.waste_kg as string),
  brandPackedG: kgToGrams0(b.brand_packed_kg as string),
  accountedG: kgToGrams0(b.accounted_kg as string),
  accountedPct: numOrNull(b.accounted_pct),
  lostG: kgToGrams0(b.lost_kg as string),
  overPacked: b.over_packed === true,
  openRuns: num(b.open_runs),
})
export const mapRecall = (r: Record<string, unknown>): RecallExport => {
  const by = obj(r.generated_by)
  const start = obj(r.start)
  return {
    code: str(r.code),
    farmers: arr(r.farmers).map((f) => ({
      farmerId: str(f.farmer_id),
      name: strOrNull(f.name),
      farmerCode: strOrNull(f.farmer_code),
    })),
    lots: arr(r.lots).map((l) => ({
      unitId: str(l.unit_id),
      code: str(l.code),
      farmerId: strOrNull(l.farmer_id),
      seasonCode: strOrNull(l.season_code),
      netG: g(l.net_kg),
      hold: str(l.hold, 'NONE'),
      status: str(l.status),
    })),
    runs: arr(r.runs).map((x) => ({
      id: str(x.id),
      code: str(x.code),
      stepCode: str(x.step_code),
      status: str(x.status),
      startedAt: iso(x.started_at),
      endedAt: iso(x.ended_at),
      notClosed: x.not_closed === true,
    })),
    batches: arr(r.batches).map((b) => ({
      unitId: str(b.unit_id),
      code: str(b.code),
      stream: str(b.stream),
      status: str(b.status),
      hold: str(b.hold, 'NONE'),
      netG: g(b.net_kg),
    })),
    otherOutputs: arr(r.other_outputs).map((o) => ({
      unitId: str(o.unit_id),
      code: str(o.code),
      unitType: str(o.unit_type),
      stream: str(o.stream),
      netG: g(o.net_kg),
    })),
    packLots: arr(r.pack_lots).map((p) => ({
      id: str(p.id),
      code: str(p.code),
      pouches: numOrNull(p.pouches),
      status: str(p.status),
      hold: str(p.hold, 'NONE'),
      pouchesDispatched: num(p.pouches_dispatched),
    })),
    customers: arr(r.customers).map((c) => ({
      orgId: str(c.org_id),
      org: str(c.org),
      packLotCode: str(c.pack_lot_code),
      challanNo: str(c.challan_no),
      pouches: num(c.pouches),
      dispatchedAt: iso(c.dispatched_at),
      shipmentCode: str(c.shipment_code),
    })),
    balance: arr(r.balance).map(mapLotBalance),
    elapsedMs: numOrNull(r.elapsed_ms),
    generatedAt: iso(r.generated_at),
    generatedBy: by.id ? { id: str(by.id), name: str(by.name), username: str(by.username) } : null,
    start: {
      code: str(start.code, str(r.code)),
      unitType: strOrNull(start.unit_type),
      packLotId: strOrNull(start.pack_lot_id),
    },
  }
}
/** Runs the mock recall (a read: nothing is held or changed). */
export function fetchRecall(code: string): Promise<RecallExport> {
  return getOne('/recall-export', mapRecall, { code: code.trim(), format: 'json' })
}
export function useRecallExport(code: string | null) {
  return useQuery({
    queryKey: [...ck.pack, 'recall', code],
    enabled: Boolean(code),
    queryFn: () => fetchRecall(code!),
    staleTime: 60_000,
    retry: false,
  })
}

/** Download the export as CSV or PDF (the browser cannot send the bearer token on a plain link). */
export async function downloadRecall(code: string, format: 'csv' | 'pdf'): Promise<void> {
  await downloadAuthed(
    buildUrl('/recall-export', { code: code.trim(), format }),
    `recall-${code.trim()}.${format}`,
  )
}

/** GET a file with the bearer token and save it. */
export async function downloadAuthed(url: string, filename: string): Promise<void> {
  const get = (token: string | undefined) =>
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
  let res = await get(tokenStore.get()?.accessToken)
  if (res.status === 401) {
    const fresh = await refreshTokens()
    if (fresh) res = await get(fresh.accessToken)
  }
  if (!res.ok) {
    let message = `Download failed (${res.status})`
    try {
      const body = obj(await res.json())
      if (typeof body.message === 'string') message = body.message
    } catch {
      /* not JSON */
    }
    throw new Error(message)
  }
  downloadBlob(filename, await res.blob())
}
